import React, { useContext, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import Separator from '../components/Separator'
import { Color, Direction } from '../utils/constants'
import MovingBackground from './MovingBackground/MovingBackground'
import { Link } from '../utils/helpers'
import ArrowButton from '../components/Button/variations/ArrowButton'
import { twMerge } from 'tailwind-merge'
import { Waypoint } from 'react-waypoint'
import HeaderContext from './Header/components/HeaderContext'
import Button from '../components/Button/Button'

interface PageHeaderProps {
  title?: string | JSX.Element
  description?: string | JSX.Element
  bgColor?: Color
  className?: string
  component?: string
  mobileComponent?: string
  link?: string
  linkTitle?: string[]
  linkExternal?: boolean
  children?: React.ReactNode
}

const PageHeader = ({
    title,
    description,
    bgColor = Color.PURPLE,
    className,
    component,
    mobileComponent,
    link,
    linkTitle,
    linkExternal = false,
    children,
}: PageHeaderProps) => {
    const [isVisible, setIsVisible] = useState(false)
    const [colors, setColors] = useState({ color: Color.BLACK, classes: 'bg-sand text-black' })
    const { headerHeight } = useContext(HeaderContext)
    useEffect(() => {
        setColors(getColors(bgColor))
    }, [bgColor])

    const getColors = (color: Color) => {
        switch (color) {
        case Color.PURPLE:
            return { classes: 'bg-purple', color: Color.WHITE }
        case Color.WHITE:
            return { classes: 'bg-white', color: Color.BLACK }
        case Color.GREEN:
            return { classes: 'bg-green', color: Color.BLACK }
        case Color.BLACK:
            return { classes: 'bg-black', color: Color.WHITE }
        case Color.GREEN_LIGHT:
            return { classes: 'bg-green-light', color: Color.BLACK }
        case Color.PURPLE_LIGHT:
            return { classes: 'bg-purple-light', color: Color.BLACK }
        case Color.SAND:
        default:
            return { classes: 'bg-sand', color: Color.BLACK }
        }
    }

    return (
        <MovingBackground
            classes={{
                wrapper: twMerge(
                    'md:side-padding flex',
                    colors.classes,
                    !children && 'side-padding min-h-screen md:h-auto md:min-h-[70vh]',
                    className,
                ),
                background: 'mix-blend-multiply',
            }}
            component={component}
            mobileComponent={mobileComponent}
        >
            <div
                className={twMerge(
                    'flex flex-col z-10 w-full',
                    link ? 'justify-between' : 'justify-end mt-auto',
                )}
                style={{ paddingTop: headerHeight }}
            >
                {link && (
                    <Link
                        to={link}
                        external={linkExternal}
                        className={twMerge(
                            'w-max flex items-center opacity-60 gap-x-6',
                            !children ? 'pb-44' : 'side-padding md:p-0',
                        )}
                    >
                        <ArrowButton direction={Direction.LEFT} buttonColor={colors.color} />
                        {linkTitle && (
                            <div className='flex gap-x-2 items-center'>
                                {linkTitle?.map((linkTitleField, index) => (
                                    <>
                                        <FormattedMessage id={linkTitleField} tagName='h6' />
                                        {index < linkTitle?.length - 1 && <h6>/</h6>}
                                    </>
                                ))}
                            </div>
                        )}
                    </Link>
                )}
                {children ?? (
                    <Waypoint onEnter={() => setIsVisible(true)}>
                        <div className='flex flex-col max-w-[1000px] pb-16 2xl:pb-44'>
                            <div
                                style={{
                                    opacity: isVisible ? 1 : 0,
                                    transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
                                    color: colors.color,
                                }}
                                className='transition--header duration-1000 ease-out'
                            >
                                {typeof title === 'string' ? (
                                    <FormattedMessage id={title}>
                                        {(value) => (
                                            <h1
                                                className='page-header--title'
                                                dangerouslySetInnerHTML={{ __html: value.toString() }}
                                            />
                                        )}
                                    </FormattedMessage>
                                ) : (
                                    title
                                )}
                            </div>
                            <div
                                style={{ width: isVisible ? '100%' : '0' }}
                                className='transition-width duration-1000 ease-out delay-500'
                            >
                                <Separator separatorColor={colors.color} className='my-8 md:my-12' />
                            </div>
                            <div
                                style={{
                                    opacity: isVisible ? 1 : 0,
                                    transform: isVisible ? 'translateY(0)' : 'translateY(-20px)',
                                    color: colors.color,
                                }}
                                className='flex transition--header duration-1000 ease-out items-center justify-between md:flex-row flex-col text-center md:text-left gap-y-4'
                            >
                                {typeof description === 'string' ? (
                                    <FormattedMessage id={description}>
                                        {(value) => (
                                            <span className='max-w-[520px] text-[20px] leading-[28px] tracking-[-0.8px]'>
                                                {value}
                                            </span>
                                        )}
                                    </FormattedMessage>
                                ) : (
                                    description
                                )}

                                {title === 'page.homepage.title' ? (
                                    <Link to='/contact'>
                                        <Button
                                            buttonColor={colors.color}
                                            chevron={Direction.RIGHT}
                                            outerClasses='w-max'
                                        >
                                            <FormattedMessage id={'page.contactUs'} tagName='h6' />
                                        </Button>
                                    </Link>
                                ) : null}
                            </div>
                        </div>
                    </Waypoint>
                )}
            </div>
        </MovingBackground>
    )
}

export default PageHeader
